import { AnimatePresence, motion } from "motion/react";
import { Check, Globe } from "lucide-react";
import { CountryFlag } from "@/components/ui/CountryFlag";

/*
 * The pieces of the plan form (PlanFlow): the profile card that fills in as a
 * student answers, the tap targets, and the Google mark. Colours are the
 * site's own (see the landing).
 */

export const C = {
  ink: "#0A0F0E",
  brand: "#00332F",
  teal: "#2EE6D6",
  tealSoft: "#CFF9F4",
  tealDeep: "#00786F",
  secondary: "#46504D",
  muted: "#69716E",
  field: "#F2F2ED",
  rule: "#E0E3DE",
  signal: "#BF4B22",
};

export const EASE = [0.22, 0.61, 0.36, 1] as const;

/** The plan's screens, in order. The homepage hero asks the first one. */
export const STEPS = ["country", "study", "timing", "language", "money", "save"] as const;
export type Step = (typeof STEPS)[number];

/** The plan's progress: one bar per screen, filled up to the current one. */
export function StepBars({ index, total }: { index: number; total: number }) {
  return (
    <div className="ml-auto flex w-28 gap-1 sm:w-36" aria-hidden="true">
      {Array.from({ length: total }, (_, i) => (
        <span key={i} className="h-1.5 flex-1 rounded-full transition-colors duration-300" style={{ backgroundColor: i <= index ? C.tealDeep : C.rule }} />
      ))}
    </div>
  );
}

export interface ProfileRow {
  label: string;
  value: string | null;
}

/**
 * The student's profile, building itself as they answer: where from, what,
 * when, where they are, languages, money. Each answer may earn one chip, a
 * fact it unlocked. Full on wide screens, a slim strip above the questions on
 * phones and tablets.
 */
export function ProfileCard({
  code,
  name,
  rows,
  chips,
  filled,
  total,
  compact = false,
}: {
  code: string | null;
  name: string | null;
  rows: ProfileRow[];
  chips: string[];
  filled: number;
  total: number;
  compact?: boolean;
}) {
  const live = chips[chips.length - 1] ?? "";
  return (
    <div
      className={`relative overflow-hidden text-white ${compact ? "rounded-[24px] px-4 py-3.5" : "rounded-[28px] p-6"}`}
      style={{ background: "linear-gradient(155deg, #00332F 0%, #003C37 55%, #05544D 100%)", boxShadow: "0 18px 50px rgba(0,51,47,0.22)" }}
    >
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.08]" style={{ backgroundImage: "radial-gradient(#2EE6D6 1px, transparent 1px)", backgroundSize: "18px 18px" }} />
      <p className="sr-only" aria-live="polite">
        {live}
      </p>

      <div className="relative flex items-center justify-between">
        <span className="text-[11px] font-bold uppercase tracking-[0.16em]" style={{ color: "rgba(255,255,255,0.55)" }}>
          Your move
        </span>
        <span className="flex gap-1" aria-label={`${filled} of ${total} answered`}>
          {Array.from({ length: total }, (_, i) => (
            <span key={i} className="h-1.5 w-4 rounded-full transition-colors duration-500" style={{ backgroundColor: i < filled ? C.teal : "rgba(255,255,255,0.16)" }} />
          ))}
        </span>
      </div>

      <div className={`relative flex items-center ${compact ? "mt-2.5 gap-2.5" : "mt-5 gap-3"}`}>
        <AnimatePresence mode="wait" initial={false}>
          <motion.span key={code ?? "none"} initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", stiffness: 420, damping: 22 }}>
            {code ? (
              <CountryFlag code={code} size={compact ? "md" : "lg"} circle animate={false} />
            ) : (
              <span className={`flex items-center justify-center rounded-full ${compact ? "h-8 w-8" : "h-12 w-12"}`} style={{ backgroundColor: "rgba(255,255,255,0.10)" }}>
                <Globe className="h-4 w-4" style={{ color: "rgba(255,255,255,0.6)" }} aria-hidden="true" />
              </span>
            )}
          </motion.span>
        </AnimatePresence>
        <span aria-hidden="true" className="h-px flex-1" style={{ backgroundImage: "linear-gradient(90deg, rgba(46,230,214,0.7) 50%, transparent 50%)", backgroundSize: "8px 1px" }} />
        <CountryFlag code="AT" size={compact ? "md" : "lg"} circle animate={false} />
      </div>
      <p className={`relative font-black tracking-[-0.03em] ${compact ? "mt-2 text-[17px]" : "mt-3 text-[24px] leading-tight"}`}>
        {name ? `${name} to Austria` : "Your way to Austria"}
      </p>

      {!compact && (
        <dl className="relative mt-5 grid grid-cols-2 gap-x-4 gap-y-3.5">
          {rows.map((r) => (
            <div key={r.label} className="min-w-0">
              <dt className="text-[11px] font-bold uppercase tracking-[0.12em]" style={{ color: "rgba(255,255,255,0.5)" }}>
                {r.label}
              </dt>
              <dd className="mt-1 min-h-[22px]">
                <AnimatePresence mode="wait" initial={false}>
                  {r.value ? (
                    <motion.span
                      key={r.value}
                      className="block truncate text-[15px] font-bold"
                      initial={{ opacity: 0, y: 6, filter: "blur(4px)" }}
                      animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.35, ease: EASE }}
                    >
                      {r.value}
                    </motion.span>
                  ) : (
                    <motion.span key="empty" className="mt-1.5 block h-2.5 w-20 rounded-full" style={{ backgroundColor: "rgba(255,255,255,0.10)" }} exit={{ opacity: 0 }} />
                  )}
                </AnimatePresence>
              </dd>
            </div>
          ))}
        </dl>
      )}

      {chips.length > 0 && (
        <div className={`relative ${compact ? "-mx-4 mt-2.5 flex gap-2 overflow-x-auto px-4 pb-0.5 [scrollbar-width:none]" : "mt-5 flex flex-wrap gap-2"}`}>
          <AnimatePresence initial={false}>
            {chips.map((c) => (
              <motion.span
                key={c}
                layout
                className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 text-[13px] font-bold"
                style={{ backgroundColor: "rgba(46,230,214,0.14)", color: C.teal }}
                initial={{ opacity: 0, scale: 0.7, y: 4 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ type: "spring", stiffness: 420, damping: 24 }}
              >
                <Check className="h-3.5 w-3.5" strokeWidth={3} aria-hidden="true" />
                {c}
              </motion.span>
            ))}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}

/** A group of tap targets that behaves as a radio group. */
export function Choices<T extends string>({
  name,
  legend,
  options,
  value,
  onChange,
  columns = 2,
  hideLegend = false,
}: {
  name: string;
  legend: string;
  options: readonly { id: T; label: string; hint?: string }[];
  value: T | null;
  onChange: (v: T) => void;
  columns?: 2 | 3 | 4;
  hideLegend?: boolean;
}) {
  const grid = columns === 4 ? "grid-cols-2 sm:grid-cols-4" : columns === 3 ? "grid-cols-3" : "grid-cols-2";
  return (
    <fieldset>
      <legend className={hideLegend ? "sr-only" : "mb-2.5 text-[15px] font-bold"} style={{ color: C.secondary }}>
        {legend}
      </legend>
      <div className={`grid gap-2.5 ${grid}`}>
        {options.map((o) => {
          const on = value === o.id;
          return (
            <label
              key={o.id}
              className="relative flex min-h-[58px] cursor-pointer flex-col justify-center rounded-2xl border-2 px-3.5 py-3 transition-[background-color,border-color,transform] duration-200 active:scale-[0.97] has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-[#00786F] has-[:focus-visible]:ring-offset-2"
              style={{ backgroundColor: on ? C.tealSoft : "#FFFFFF", borderColor: on ? C.tealDeep : C.rule }}
            >
              <input type="radio" name={name} value={o.id} checked={on} onChange={() => onChange(o.id)} className="sr-only" />
              <span className="pr-6 text-[15px] font-bold leading-snug sm:text-[16px]" style={{ color: C.brand }}>
                {o.label}
              </span>
              {o.hint && (
                <span className="mt-0.5 pr-6 text-[13px] leading-snug" style={{ color: C.muted }}>
                  {o.hint}
                </span>
              )}
              <AnimatePresence>
                {on && (
                  <motion.span
                    className="absolute right-3 top-3 flex h-5 w-5 items-center justify-center rounded-full"
                    style={{ backgroundColor: C.tealDeep }}
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    exit={{ scale: 0 }}
                    transition={{ type: "spring", stiffness: 500, damping: 22 }}
                    aria-hidden="true"
                  >
                    <Check className="h-3 w-3 text-white" strokeWidth={3.5} />
                  </motion.span>
                )}
              </AnimatePresence>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}

/** Google's "G", for the sign-in button (their brand rules ask for the coloured mark). */
export function GoogleIcon({ className = "h-5 w-5" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z" />
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.3 0-9.7-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" />
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.2-.1-2.3-.4-3.5z" />
    </svg>
  );
}
